import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  X, ChevronLeft, ChevronRight, ChevronUp, Plus, Minus,
  Users, Bath, Waves, Baby, Snowflake, WashingMachine, Wifi, Car, Clock, Plane,
  CigaretteOff, PawPrint, VolumeX, Instagram, Facebook, MessageCircle, Mail,
  BadgeEuro, ShieldCheck, HeartHandshake, Sparkles, Percent, Headphones, type LucideIcon,
} from "lucide-react";
const GalleryCarousel = lazy(() => import("@/components/GalleryCarousel"));
import {
  GALLERY,
  GALLERY_EMPTY_LABEL,
  GALLERY_CATEGORY_LABELS,
  GALLERY_SUBS,
  GALLERY_SUB_LABELS,
  HERO,
  LANGS,
  RATES,
  REVIEW_AGGREGATE,
  T,
  TESTIMONIALS,
  type Lang,
  type GalleryCategory,
  type GallerySub,
} from "@/lib/villa-content";
import { getPricingSettings } from "@/lib/pricing.functions";
import { listPublishedReviews } from "@/lib/reviews.functions";


const H1_DESCRIPTOR: Record<Lang, string> = {
  en: "Casa Mi Pajobe, Private Villa with Pool in La Nucia",
  nl: "Casa Mi Pajobe, Privévilla met zwembad in La Nucia",
  fr: "Casa Mi Pajobe, Villa privée avec piscine à La Nucia",
  de: "Casa Mi Pajobe, Privatvilla mit Pool in La Nucia",
};

/** Convert "dd.mm.yyyy" → "yyyy-mm-dd" for /book?from=...&to=... */
function periodStartISO(period: string): string {
  const start = period.split("–")[0].trim();
  const [d, m, y] = start.split(".");
  return `${y}-${m}-${d}`;
}

function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function PeriodBookLink({ period, lang }: { period: string; lang: Lang }) {
  const from = periodStartISO(period);
  const to = addDaysISO(from, 7);
  return (
    <Link
      to="/book/$lang"
      params={{ lang }}
      search={{ from, to, bedrooms: 4 }}
      className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition hover:border-primary hover:text-primary"
    >
      Book →
    </Link>
  );
}

function LastMinuteBar({
  lang,
  percent,
  withinDays,
  label,
}: {
  lang: Lang;
  percent: number;
  withinDays: number;
  label: string;
}) {
  const cutoff = useMemo(() => {
    // Fixed last-minute cut-off: 11 July 2026.
    const d = new Date(2026, 6, 11);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [withinDays]);

  // Time-dependent output must not render during SSR: the server and the client
  // never agree on the current second (React hydration error #418).
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const localeMap: Record<Lang, string> = { en: "en-GB", nl: "nl-BE", fr: "fr-FR", de: "de-DE" };
  const cutoffStr = cutoff.toLocaleDateString(localeMap[lang] ?? "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  if (now === null) return <div aria-hidden style={{ minHeight: 26 }} />;
  const diff = cutoff.getTime() - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1000);
  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <a href="#pricing" className="block bg-red-300 text-red-950 hover:bg-red-400 transition">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-2 px-6 py-1 text-center text-xs font-medium leading-tight">
        <span aria-hidden>★</span>
        <span>
          {label}: −{percent}%
          <span className="mx-2 opacity-60">·</span>
          <span className="opacity-90">(check-out ≤ {cutoffStr})</span>
        </span>
        <span className="mx-2 opacity-60" aria-hidden>·</span>
        <span className="font-mono tabular-nums">
          {days}d {pad(hours)}:{pad(minutes)}:{pad(seconds)}
        </span>
      </div>
    </a>
  );
}

export function VillaHome({ lang }: { lang: Lang }) {
  const t = T[lang];
  const [mapZoom, setMapZoom] = useState(10);
  const fetchPricing = useServerFn(getPricingSettings);
  const { data: pricing } = useQuery({
    queryKey: ["pricing-settings"],
    queryFn: () => fetchPricing(),
    staleTime: 5 * 60_000,
  });
  const liveRates = pricing && pricing.rates.length ? pricing.rates : RATES;
  const fetchPublishedReviews = useServerFn(listPublishedReviews);
  const { data: siteReviews } = useQuery({
    queryKey: ["published-reviews"],
    queryFn: () => fetchPublishedReviews(),
    staleTime: 5 * 60_000,
  });

  const lastMinuteLabel =
    (pricing?.lastMinuteLabel as Record<string, string> | undefined)?.[lang] ??
    pricing?.lastMinuteLabel?.en ??
    "Last-minute deal";
  useEffect(() => {
    if (typeof window === "undefined") return;
    const targets = Array.from(
      document.querySelectorAll<HTMLElement>("main section, main > section, [data-reveal]")
    );
    // Fall back: grab all sections inside the villa home root.
    const sections = targets.length
      ? targets
      : Array.from(document.querySelectorAll<HTMLElement>("section"));
    // Skip the hero (first section) so it stays visible immediately.
    const [, ...rest] = sections;
    rest.forEach((el) => el.classList.add("reveal"));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal-in");
          } else {
            e.target.classList.remove("reveal-in");
          }
        });
      },
      { threshold: 0.01, rootMargin: "0px 0px 25% 0px" }
    );
    rest.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [lang]);
  return (
    <div className="min-h-screen bg-background text-foreground" lang={lang}>
      <SiteHeader t={t} lang={lang} />

      <section className="relative isolate">
        <img
          src={HERO}
          alt="Casa Mi Pajobe villa with private pool, La Nucia, Costa Blanca"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="absolute inset-0 -z-10 bg-hero-gradient" />
        <div className="mx-auto flex min-h-[88vh] max-w-6xl flex-col justify-end px-6 pb-20 pt-32 sm:pb-28">
          <p className="mb-4 text-center sm:text-left text-sm font-medium uppercase tracking-[0.25em] text-white/85 text-shadow-soft whitespace-pre-line">
            {t.heroEyebrow}
          </p>
          <h1 className="text-shadow-soft max-w-3xl text-center sm:text-left text-5xl font-semibold leading-[1.05] text-white sm:text-6xl md:text-7xl">
            {H1_DESCRIPTOR[lang]}
          </h1>
          <p className="text-shadow-soft mt-5 max-w-2xl text-center sm:text-left text-lg text-white/90 sm:text-xl">
            {t.heroSub}
          </p>
          <div className="mt-8 flex flex-wrap justify-center sm:justify-start gap-3">
            <Link
              to="/book/$lang"
              params={{ lang }}
              className="inline-flex items-center justify-center rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground shadow-lg shadow-black/20 transition hover:brightness-110"
            >
              {t.heroPrimary}
            </Link>
            <a
              href="#gallery"
              className="inline-flex items-center justify-center rounded-full border border-white/60 bg-white/10 px-7 py-3 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
            >
              {t.heroSecondary}
            </a>
          </div>
        </div>
      </section>

      <aside className="mx-auto max-w-3xl px-6 pt-12 sm:pt-16">
        <figure className="border-y border-primary/60 py-8 text-center">
          <blockquote className="text-xl italic leading-relaxed text-foreground sm:text-2xl">
            &ldquo;{t.heroQuote}&rdquo;
          </blockquote>
          <figcaption className="mt-4 text-sm font-medium uppercase tracking-[0.15em] text-muted-foreground">
            {t.heroQuoteAttribution}
          </figcaption>
        </figure>
      </aside>

      <section id="about" className="mx-auto max-w-6xl px-6 py-16 sm:py-24">
        <p className="text-center sm:text-left text-sm font-medium uppercase tracking-[0.2em] text-primary">
          {t.aboutEyebrow}
        </p>
        <div className="mt-6 grid items-center gap-10 md:grid-cols-[42fr_58fr] md:gap-12">
          <div className="text-center sm:text-left">
            <h2 className="text-4xl font-semibold leading-tight sm:text-5xl">{t.aboutTitle}</h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-muted-foreground">
              <p>{t.aboutP1}</p>
              <p>{t.aboutP2}</p>
              <p>{t.aboutP3}</p>
            </div>
          </div>
          <div>
            <AtAGlanceCard t={t} />
          </div>
        </div>
      </section>

      <section className="bg-secondary/60 py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-10 sm:mb-14 max-w-2xl text-center sm:text-left mx-auto sm:mx-0">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">{t.featuresEyebrow}</p>
            <h2 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl">{t.featuresTitle}</h2>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {t.features.map((f, i) => {
              const targets = [
                "#gallery=pool",
                "#gallery=outdoor_kitchen",
                "#gallery=bedrooms",
                "#gallery=garden",
                "#location",
                "#gallery",
              ];
              const href = targets[i] ?? "#gallery";
              return (
                <a
                  key={f.title}
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.location.hash !== href) {
                      history.replaceState(null, "", `${window.location.pathname}${window.location.search}${href}`);
                      window.dispatchEvent(new HashChangeEvent("hashchange"));
                    }
                    document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="block rounded-2xl border border-border/70 bg-card p-7 text-center shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:text-left"
                >
                  <h3 className="text-xl font-semibold text-foreground">{f.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">{f.body}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section id="gallery" className="mx-auto max-w-6xl px-6 py-16 sm:py-24">
        <div className="mb-10 sm:mb-12 flex flex-col items-center gap-4 text-center sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:text-left" data-gallery-header>
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">{t.galleryEyebrow}</p>
            <h2 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl">{t.galleryTitle}</h2>
          </div>
          <p className="max-w-md leading-relaxed text-muted-foreground">{t.galleryLead}</p>
        </div>
        <GalleryWithFilters lang={lang} />
      </section>

      <section id="reviews" className="bg-secondary/60 py-16 sm:py-24">
        {(() => {
          const labels: Record<Lang, { featured: string; more: string; mostRecent: string; average: string; verified: string }> = {
            en: { featured: "Featured reviews", more: "More reviews", mostRecent: "Most recent", average: "average", verified: "verified reviews" },
            nl: { featured: "Uitgelichte beoordelingen", more: "Meer beoordelingen", mostRecent: "Meest recent", average: "gemiddeld", verified: "geverifieerde beoordelingen" },
            fr: { featured: "Avis à la une", more: "Plus d'avis", mostRecent: "Plus récent", average: "moyenne", verified: "avis vérifiés" },
            de: { featured: "Hervorgehobene Bewertungen", more: "Weitere Bewertungen", mostRecent: "Neueste", average: "Durchschnitt", verified: "verifizierte Bewertungen" },
          };
          const L = labels[lang];
          const siteAsTestimonials = (siteReviews ?? []).map((r) => ({
            initials: (r.guestName[0] ?? "G").toUpperCase(),
            name: r.guestName,
            country: r.country ?? "",
            countryFlag: "",
            date: r.date,
            quoteLang: (["en", "nl", "fr", "de"].includes(r.locale) ? r.locale : "nl") as Lang,
            quote: { en: r.comment, nl: r.comment, fr: r.comment, de: r.comment } as Record<Lang, string>,
            source: "casamipajobe.com",
            rating: r.rating,
          }));
          const all = [...siteAsTestimonials, ...TESTIMONIALS];
          const sorted = [...all].sort((a, b) => b.date.localeCompare(a.date));
          const featured = sorted.slice(0, 3);
          const rest = sorted.slice(3);
          const sources = Array.from(new Set(all.map((r) => r.source))).join(" & ");
          const avg =
            Math.round(
              (all.reduce((s, r) => s + r.rating, 0) / Math.max(all.length, 1) +
                Number.EPSILON) * 10,
            ) / 10;
          const aggregate = siteAsTestimonials.length
            ? { ratingValue: avg, reviewCount: all.length }
            : REVIEW_AGGREGATE;

          const fmt = (d: string) => new Date(d + "-01").toLocaleDateString(lang, { month: "short", year: "numeric" });
          return (
            <div className="mx-auto max-w-6xl px-6 sm:px-10">
              <div className="mb-8 max-w-2xl text-center sm:text-left mx-auto sm:mx-0">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary">{t.testimonialsEyebrow}</p>
                <h2 className="mt-2 text-3xl font-semibold leading-tight sm:text-4xl">{t.testimonialsTitle}</h2>
              </div>

              <div className="mb-10 flex flex-col items-center gap-2 text-center">
                <span className="text-3xl tracking-widest text-amber-500 sm:text-4xl">★★★★★</span>
                <p className="text-xl font-semibold text-foreground sm:text-2xl">
                  {aggregate.ratingValue} {L.average}
                  <span className="mx-2 text-muted-foreground">·</span>
                  {aggregate.reviewCount} {L.verified}

                </p>
                <p className="text-sm text-muted-foreground">{sources}</p>
              </div>

              <p className="mb-3 text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">{L.featured}</p>
              <ul className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {featured.map((r, idx) => (
                  <li
                    key={r.name + r.date}
                    className={`flex flex-col rounded-2xl bg-card p-5 ${idx === 0 ? "border-2 border-primary" : "border border-border/60"}`}
                  >
                    {idx === 0 && (
                      <span className="mb-2 inline-block w-fit rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                        {L.mostRecent}
                      </span>
                    )}
                    <div aria-hidden className="font-serif text-3xl leading-none text-primary">“</div>
                    <blockquote lang={lang} className="mt-1 flex-1 text-sm leading-relaxed text-foreground">
                      {r.quote[lang]}
                    </blockquote>
                    <div className="mt-4 flex items-center gap-3 border-t border-border/60 pt-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                        {r.initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-foreground">
                          {r.name} <span className="font-normal text-muted-foreground" aria-hidden>{r.countryFlag}</span>{" "}
                          <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{r.source}</span>
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          <span className="text-amber-500">{"★".repeat(r.rating)}</span> · {fmt(r.date)}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {rest.length > 0 && (
                <>
                  <p className="mb-3 text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">{L.more}</p>
                  <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                    {rest.map((r) => (
                      <li key={r.name + r.date} className="rounded-xl border border-border/60 bg-card p-4">
                        <p lang={lang} className="text-xs leading-relaxed text-muted-foreground">
                          “{r.quote[lang]}”
                        </p>
                        <p className="mt-2 text-[11px] font-medium text-foreground">
                          {r.name} <span className="font-normal text-muted-foreground" aria-hidden>{r.countryFlag}</span>
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          <span className="text-amber-500">{"★".repeat(r.rating)}</span> · {fmt(r.date)} · {r.source}
                        </p>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          );
        })()}
      </section>

      <section id="location" className="bg-secondary/60 py-16 sm:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 md:grid-cols-2 md:gap-16">
          <div className="text-center sm:text-left">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">{t.locationEyebrow}</p>
            <h2 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl">{t.locationTitle}</h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{t.locationLead}</p>
            <dl className="mt-8 grid grid-cols-2 gap-x-8 gap-y-5 text-sm">
              {t.locationItems.map(([k, v]) => (
                <div key={k} className="border-t border-border/70 pt-3">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="mt-1 text-lg font-medium text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-border/70 shadow-sm">
            <iframe
              title="Google Map of La Nucia, Costa Blanca"
              src={`https://maps.google.com/maps?q=38.6160,-0.1278&z=${mapZoom}&hl=en&output=embed`}
              className="h-full min-h-[420px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute right-3 top-3 flex flex-col overflow-hidden rounded-lg border border-border/70 bg-background/95 shadow-md backdrop-blur">
              <button
                type="button"
                onClick={() => setMapZoom((z) => Math.min(21, z + 1))}
                disabled={mapZoom >= 21}
                aria-label="Zoom in"
                className="flex h-9 w-9 items-center justify-center text-foreground transition hover:bg-secondary disabled:opacity-40"
              >
                <Plus className="h-4 w-4" />
              </button>
              <div className="h-px bg-border/70" />
              <button
                type="button"
                onClick={() => setMapZoom((z) => Math.max(1, z - 1))}
                disabled={mapZoom <= 1}
                aria-label="Zoom out"
                className="flex h-9 w-9 items-center justify-center text-foreground transition hover:bg-secondary disabled:opacity-40"
              >
                <Minus className="h-4 w-4" />
              </button>
            </div>
            <div className="absolute bottom-3 right-3 rounded-md bg-background/90 px-2 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur">
              Zoom {mapZoom}
            </div>
          </div>
        </div>
      </section>

      <section id="surroundings" className="mx-auto max-w-6xl px-6 py-16 sm:py-24">
        <div className="text-center sm:text-left">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">{t.surroundingsEyebrow}</p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl">{t.surroundingsTitle}</h2>
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {t.surroundingsBody.map((p) => (
            <p key={p.slice(0, 24)} className="text-lg leading-relaxed text-muted-foreground">
              {p}
            </p>
          ))}
        </div>
      </section>

      <section id="pricing" className="mx-auto max-w-6xl px-6 py-16 sm:py-24">
        <div className="text-center sm:text-left">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">{t.pricingEyebrow}</p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl">{t.pricingTitle}</h2>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground sm:mx-0 mx-auto">{t.ratesIntro}</p>
          {pricing?.lastMinuteEnabled && pricing.lastMinutePercent > 0 && (
            (() => {
              // Fixed last-minute cut-off: 11 July 2026.
              const cutoff = new Date(2026, 6, 11);
              cutoff.setHours(23, 59, 59, 999);
              if (cutoff.getTime() < Date.now()) return null;
              const localeMap: Record<Lang, string> = { en: "en-GB", nl: "nl-BE", fr: "fr-FR", de: "de-DE" };
              const cutoffStr = cutoff.toLocaleDateString(localeMap[lang] ?? "en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              });
              return (
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
                  <span aria-hidden>★</span>
                  <span>
                    {lastMinuteLabel}: −{pricing.lastMinutePercent}%{" "}
                    <span className="font-normal opacity-75">
                      (check-out ≤ {cutoffStr})
                    </span>
                  </span>
                </div>
              );
            })()
          )}
        </div>

        {/* Mobile: stacked cards (no horizontal scroll) */}
        <div className="mt-10 space-y-4 sm:hidden">
          {liveRates.map((row) => (
            <div
              key={row.period}
              className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm"
            >
              <p className="text-center text-base font-semibold text-foreground">{row.period}</p>
              <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
                {[
                  { label: t.ratesBedroomsLabel(2), value: row.beds2 },
                  { label: t.ratesBedroomsLabel(3), value: row.beds3 },
                  { label: t.ratesBedroomsLabel(4), value: row.beds4, strong: true },
                ].map((c) => (
                  <div key={c.label} className="rounded-xl bg-secondary/40 px-2 py-3">
                    <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {c.label}
                    </dt>
                    <dd
                      className={`mt-1 tabular-nums text-foreground ${c.strong ? "font-semibold" : ""}`}
                    >
                      {c.value != null ? `€${c.value}` : (
                        <span className="text-xs text-muted-foreground">{t.ratesNotAvailable}</span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
          <p className="px-1 text-xs text-muted-foreground">
            {t.ratesPerWeek.replace(/^\/\s*/, "")} · EUR
          </p>
        </div>

        {/* sm+ : original table */}
        <div className="mt-10 hidden overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm sm:block">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/70 bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-4 font-medium">{t.ratesColPeriod}</th>
                  <th className="px-5 py-4 text-right font-medium">{t.ratesBedroomsLabel(2)}</th>
                  <th className="px-5 py-4 text-right font-medium">{t.ratesBedroomsLabel(3)}</th>
                  <th className="px-5 py-4 text-right font-medium">{t.ratesBedroomsLabel(4)}</th>
                </tr>
              </thead>
              <tbody>
                {liveRates.map((row) => (
                  <tr key={row.period} className="border-b border-border/50 last:border-0">
                    <td className="px-5 py-4 font-medium text-foreground">{row.period}</td>
                    <td className="px-5 py-4 text-right tabular-nums text-foreground">
                      {row.beds2 != null ? `€${row.beds2}` : <span className="text-muted-foreground">{t.ratesNotAvailable}</span>}
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums text-foreground">
                      {row.beds3 != null ? `€${row.beds3}` : <span className="text-muted-foreground">{t.ratesNotAvailable}</span>}
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums font-semibold text-foreground">
                      {row.beds4 != null ? `€${row.beds4}` : <span className="text-muted-foreground">{t.ratesNotAvailable}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-secondary/30 text-xs text-muted-foreground">
                  <td className="px-5 py-3" colSpan={4}>{t.ratesPerWeek.replace(/^\/\s*/, "")} · EUR</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-border/70 bg-card p-7">
            <h3 className="text-lg font-semibold text-foreground text-center sm:text-left">{t.ratesIncludedTitle}</h3>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {t.ratesIncluded.map((line) => (
                <li key={line} className="flex gap-2">
                  <span aria-hidden className="mt-[6px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border/70 bg-card p-7">
            <h3 className="text-lg font-semibold text-foreground text-center sm:text-left">{t.ratesNotIncludedTitle}</h3>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {t.ratesNotIncluded.map((line) => (
                <li key={line} className="flex gap-2">
                  <span aria-hidden className="mt-[6px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground text-center sm:text-left">{t.ratesStayInfoTitle}</h3>
            <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-muted-foreground">
              {t.pricingLeft.map((line) => <li key={line}>• {line}</li>)}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground text-center sm:text-left">{t.ratesHouseRulesTitle}</h3>
            <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-muted-foreground">
              {t.pricingRight.map((line) => <li key={line}>• {line}</li>)}
            </ul>
          </div>
        </div>

        <WhyBookDirectSection lang={lang} />

        <div className="mt-12 rounded-2xl border border-border/70 bg-card p-8 sm:p-10">
          <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
            <div>
              <h3 className="text-2xl font-semibold">{t.ctaTitle}</h3>
              <p className="mt-2 text-muted-foreground">{t.ctaBody}</p>
            </div>
            <Link
              to="/book/$lang"
              params={{ lang }}
              className="inline-flex items-center justify-center rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground transition hover:brightness-110"
            >
              {t.ctaButton}
            </Link>
          </div>
        </div>
      </section>

      <FAQSection t={t} />

      <SiteFooter t={t} lang={lang} />
    </div>
  );
}

const WHY_BOOK_DIRECT: Record<Lang, { eyebrow: string; title: string; lead: string; items: { title: string; body: string }[] }> = {
  en: {
    eyebrow: "Book direct",
    title: "Why book directly through our website",
    lead: "Skip the middleman. You get the best price, personal service from the owners, and full flexibility.",
    items: [
      { title: "Best price guaranteed", body: "No platform commission means we pass the savings on to you — always the lowest rate." },
      { title: "No booking fees", body: "Zero service or cleaning surcharges on top of the published rate. What you see is what you pay." },
      { title: "Direct contact with the owners", body: "Talk to us — not a call center. We know every corner of Casa Mi Pajobe." },
      { title: "Flexible & personal", body: "Tailored arrival times, extras and local tips arranged directly with us." },
      { title: "Secure payment", body: "Pay safely by card or transfer via our protected checkout — fully encrypted." },
      { title: "Trusted by guests", body: "Verified reviews from real renters who booked the villa directly." },
    ],
  },
  nl: {
    eyebrow: "Boek rechtstreeks",
    title: "Waarom rechtstreeks boeken via onze website",
    lead: "Sla de tussenpersoon over. U krijgt de beste prijs, persoonlijke service van de eigenaars en volledige flexibiliteit.",
    items: [
      { title: "Beste prijsgarantie", body: "Geen platformcommissie — wij geven die korting rechtstreeks aan u door." },
      { title: "Geen boekingskosten", body: "Geen extra service- of schoonmaakkosten bovenop de prijs. Wat u ziet, is wat u betaalt." },
      { title: "Rechtstreeks contact met de eigenaars", body: "U spreekt met ons — geen callcenter. Wij kennen elke hoek van Casa Mi Pajobe." },
      { title: "Flexibel & persoonlijk", body: "Aankomsttijden, extra's en lokale tips regelt u rechtstreeks met ons." },
      { title: "Veilige betaling", body: "Betaal veilig via kaart of overschrijving in onze beveiligde checkout." },
      { title: "Vertrouwd door huurders", body: "Geverifieerde reviews van échte huurders die rechtstreeks boekten." },
    ],
  },
  fr: {
    eyebrow: "Réservation directe",
    title: "Pourquoi réserver directement sur notre site",
    lead: "Sans intermédiaire : meilleur prix, service personnel des propriétaires et flexibilité totale.",
    items: [
      { title: "Meilleur prix garanti", body: "Pas de commission de plateforme — nous vous reversons l'économie." },
      { title: "Aucun frais de réservation", body: "Pas de frais de service ni de ménage cachés. Le prix affiché est le prix payé." },
      { title: "Contact direct avec les propriétaires", body: "Vous nous parlez directement — pas un centre d'appels." },
      { title: "Souple & personnel", body: "Horaires d'arrivée, extras et conseils locaux organisés avec nous." },
      { title: "Paiement sécurisé", body: "Carte ou virement via notre checkout entièrement chiffré." },
      { title: "Approuvé par les locataires", body: "Avis vérifiés de vrais locataires ayant réservé en direct." },
    ],
  },
  de: {
    eyebrow: "Direkt buchen",
    title: "Warum direkt über unsere Website buchen",
    lead: "Ohne Zwischenhändler: bester Preis, persönlicher Service der Eigentümer und volle Flexibilität.",
    items: [
      { title: "Bestpreisgarantie", body: "Keine Plattformprovision — die Ersparnis geben wir an Sie weiter." },
      { title: "Keine Buchungsgebühren", body: "Keine versteckten Service- oder Reinigungszuschläge." },
      { title: "Direkter Kontakt zu den Eigentümern", body: "Sie sprechen mit uns — kein Callcenter." },
      { title: "Flexibel & persönlich", body: "Anreisezeiten, Extras und lokale Tipps direkt mit uns abgestimmt." },
      { title: "Sichere Zahlung", body: "Karte oder Überweisung über unseren verschlüsselten Checkout." },
      { title: "Von Gästen empfohlen", body: "Verifizierte Bewertungen echter Direktbucher." },
    ],
  },
};

const WHY_BOOK_ICONS: LucideIcon[] = [BadgeEuro, Percent, HeartHandshake, Sparkles, ShieldCheck, Headphones];

function WhyBookDirectSection({ lang }: { lang: Lang }) {
  const c = WHY_BOOK_DIRECT[lang];
  return (
    <div className="mt-16">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{c.eyebrow}</p>
        <h3 className="mt-2 text-2xl font-semibold sm:text-3xl">{c.title}</h3>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">{c.lead}</p>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {c.items.map((item, i) => {
          const Icon = WHY_BOOK_ICONS[i] ?? Sparkles;
          return (
            <div key={item.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h4 className="mt-4 text-base font-semibold text-foreground">{item.title}</h4>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const GLANCE_ICONS: LucideIcon[] = [
  Users, Bath, Waves, Baby, Snowflake, WashingMachine, Wifi, Car, Clock, Plane,
];
const RULE_ICONS: LucideIcon[] = [CigaretteOff, PawPrint, VolumeX];

function AtAGlanceCard({ t }: { t: (typeof T)["en"] }) {
  return (
    <aside className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm sm:p-7">
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {t.glance.eyebrow}
      </p>
      <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {t.glance.tiles.map((tile, i) => {
          const Icon = GLANCE_ICONS[i] ?? Users;
          return (
            <li
              key={tile.title}
              className="flex flex-col gap-2 rounded-xl border border-border/60 bg-background p-3"
            >
              <span
                aria-hidden
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full"
                style={{ backgroundColor: "rgba(196, 119, 58, 0.14)" }}
              >
                <Icon className="h-4 w-4" style={{ color: "#C4773A" }} />
              </span>
              <div className="min-w-0">
                <p className="text-[13px] font-medium leading-tight text-foreground">{tile.title}</p>
                <p className="mt-1 text-[11.5px] leading-snug text-muted-foreground">{tile.sub}</p>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-6 border-t border-border/60 pt-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {t.glance.rulesTitle}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {t.glance.rules.map((rule, i) => {
            const Icon = RULE_ICONS[i] ?? VolumeX;
            return (
              <span
                key={rule}
                className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-700"
              >
                <Icon className="h-3.5 w-3.5" />
                {rule}
              </span>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function FAQSection({ t }: { t: typeof T["en"] }) {
  return (
    <section id="faq" className="bg-secondary/60 py-16 sm:py-24">
      <div className="mx-auto max-w-4xl px-6">
        <div className="mb-10 max-w-2xl text-center sm:text-left mx-auto sm:mx-0">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">{t.faqEyebrow}</p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl">{t.faqTitle}</h2>
          <p className="mt-5 text-lg text-muted-foreground">{t.faqLead}</p>
        </div>
        <div className="divide-y divide-border/70 overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
          {t.faq.map((item) => (
            <details key={item.q} className="group p-6 open:bg-secondary/30">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-medium text-foreground">
                <span>{item.q}</span>
                <span aria-hidden className="text-2xl leading-none text-primary transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function SiteHeader({ t, lang }: { t: typeof T["en"]; lang: Lang }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const textColor = scrolled ? "text-foreground" : "text-white";
  const navColor = scrolled ? "text-foreground/80" : "text-white/90";
  const navHover = scrolled ? "hover:text-foreground" : "hover:text-white";
  return (
    <header className="fixed inset-x-0 top-3 z-30 sm:top-5">
      <div
        className={`mx-3 sm:mx-auto flex max-w-6xl items-center justify-between gap-3 rounded-full px-5 py-3 sm:px-6 sm:py-3.5 transition-[background-color,box-shadow,backdrop-filter] duration-500 ease-out ${
          scrolled ? "bg-background/85 shadow-sm backdrop-blur-md" : ""
        }`}
      >
        <Link
          to="/$lang"
          params={{ lang }}
          className={`font-display text-lg font-semibold tracking-wide whitespace-nowrap sm:text-xl transition-colors duration-500 ease-out ${textColor}`}
        >
          Casa Mi Pajobe
        </Link>
        <nav className={`hidden gap-6 text-sm lg:flex xl:gap-8 transition-colors duration-500 ease-out ${navColor}`}>
          <a href="#about" className={`transition ${navHover}`}>{t.navVilla}</a>
          <a href="#gallery" className={`transition ${navHover}`}>{t.navGallery}</a>
          <a href="#reviews" className={`transition ${navHover}`}>{t.navReviews}</a>
          <a href="#location" className={`transition ${navHover}`}>{t.navLocation}</a>
          <a href="#pricing" className={`transition ${navHover}`}>{t.navRates}</a>
          <a href="#faq" className={`transition ${navHover}`}>{t.faqEyebrow}</a>
          <Link to="/guide" className={`transition ${navHover}`}>{t.navGuide}</Link>
        </nav>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <div className={`hidden items-center gap-1 sm:flex transition-colors duration-500 ease-out ${navColor}`}>
            <a
              href="https://www.instagram.com/casa_mi_pajobe/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className={`rounded-full p-2 transition ${navHover}`}
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href="https://www.facebook.com/casamipajobe/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className={`rounded-full p-2 transition ${navHover}`}
            >
              <Facebook className="h-4 w-4" />
            </a>
          </div>
          <Link
            to="/"
            search={{ picker: true }}
            title={t.changeLanguage}
            aria-label={t.changeLanguage}
            className={`hidden items-center gap-2 rounded-full border px-3 py-1.5 text-sm backdrop-blur transition-colors duration-500 ease-out sm:inline-flex ${
              scrolled
                ? "border-border bg-background/60 text-foreground hover:bg-background"
                : "border-white/40 bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            <span aria-hidden>{LANGS.find((l) => l.code === lang)?.flag}</span>
            <span className="hidden md:inline">{LANGS.find((l) => l.code === lang)?.label}</span>
          </Link>
          <Link
            to="/book/$lang"
            params={{ lang }}
            className="rounded-full bg-white/95 px-4 py-2 text-sm font-medium text-primary transition hover:bg-white sm:px-5"
          >
            {t.bookCta}
          </Link>
        </div>
      </div>
    </header>
  );
}

function SiteFooter({ t, lang }: { t: typeof T["en"]; lang: Lang }) {
  return (
    <footer className="border-t border-border/70 bg-secondary/40">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-14 md:grid-cols-3">
        <div>
          <p className="font-display text-xl font-semibold">Casa Mi Pajobe</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Urb. Panorama, La Nucia · Costa Blanca · Spain
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{t.footerExplore}</p>
          <ul className="mt-3 space-y-2">
            <li><a href="#about" className="hover:text-foreground">{t.navVilla}</a></li>
            <li><a href="#gallery" className="hover:text-foreground">{t.navGallery}</a></li>
            <li><a href="#reviews" className="hover:text-foreground">{t.navReviews}</a></li>
            <li><a href="#location" className="hover:text-foreground">{t.navLocation}</a></li>
            <li><a href="#pricing" className="hover:text-foreground">{t.navRates}</a></li>
            <li><a href="#faq" className="hover:text-foreground">{t.navFaq}</a></li>
          </ul>
          <p className="mt-4">
            <Link to="/" search={{ picker: true }} className="hover:text-foreground">{t.changeLanguage}</Link>
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{t.footerContact}</p>
          <p className="mt-3">
            {t.footerContactBody(
              <Link
                to="/book/$lang"
                params={{ lang }}
                className="font-medium text-primary hover:underline"
              >
                {t.ctaButton.toLowerCase()}
              </Link>
            )}
          </p>
          <div className="mt-4 flex items-center gap-3">
            <a
              href="https://www.instagram.com/casa_mi_pajobe/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="rounded-full border border-border bg-background p-2 text-foreground/80 transition hover:text-foreground hover:bg-secondary"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href="https://www.facebook.com/casamipajobe/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="rounded-full border border-border bg-background p-2 text-foreground/80 transition hover:text-foreground hover:bg-secondary"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a
              href="https://wa.me/32475921693"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="rounded-full border border-border bg-background p-2 text-foreground/80 transition hover:text-foreground hover:bg-secondary"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
            <a
              href="mailto:info@casamipajobe.com"
              aria-label="Email"
              className="rounded-full border border-border bg-background p-2 text-foreground/80 transition hover:text-foreground hover:bg-secondary"
            >
              <Mail className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-6 pb-6">
        <p className="whitespace-pre-line text-xs text-muted-foreground">{t.footerLicense}</p>
      </div>
      <div className="border-t border-border/70 py-4 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} Casa Mi Pajobe · Benjamin Rysman. All rights reserved.
        {" · "}
        <a href="/auth" rel="nofollow" className="underline-offset-2 hover:underline">
          Admin
        </a>
      </div>
    </footer>
  );
}
function GalleryWithFilters({ lang }: { lang: Lang }) {
  const labels = GALLERY_CATEGORY_LABELS[lang];
  const categories: (GalleryCategory | "all")[] = [
    "pool",
    "garden",
    "outdoor_kitchen",
    "naya",
    "bedrooms",
    "bathrooms",
    "living_room",
    "kitchen",
    "driveway",
    "all",
  ];
  const [active, setActive] = useState<GalleryCategory | "all">("pool");
  const [activeSub, setActiveSub] = useState<GallerySub | "all">("all");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    const parse = () => {
      const hash = window.location.hash; // e.g. "#gallery=pool"
      const match = hash.match(/#gallery=([a-z_]+)/);
      if (match) {
        const cat = match[1] as GalleryCategory;
        if (categories.includes(cat)) setActive(cat);
      } else if (hash === "#gallery") {
        setActive("all");
      }
    };
    parse();
    window.addEventListener("hashchange", parse);
    return () => window.removeEventListener("hashchange", parse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectCategory = (cat: GalleryCategory | "all") => {
    setActive(cat);
    const newHash = cat === "all" ? "#gallery" : `#gallery=${cat}`;
    if (window.location.hash !== newHash) {
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
    }
    const section = document.getElementById("gallery");
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
  };


  const filtered = useMemo(
    () => {
      if (active === "all") return GALLERY;
      const base = GALLERY.filter((g) => g.category === active);
      if ((active === "bedrooms" || active === "bathrooms") && activeSub !== "all") {
        return base.filter((g) => g.sub === activeSub);
      }
      return base;
    },
    [active, activeSub],
  );

  useEffect(() => {
    setActiveSub("all");
  }, [active]);

  const subs: GallerySub[] | null =
    active === "bedrooms" ? GALLERY_SUBS.bedrooms : active === "bathrooms" ? GALLERY_SUBS.bathrooms : null;

  useEffect(() => {
    setLightboxIndex(null);
  }, [active]);

  const viewable = useMemo(
    () => filtered.filter((g) => !g.placeholder && g.src),
    [filtered],
  );

  const openLightbox = (img: { src?: string; placeholder?: boolean }) => {
    if (img.placeholder || !img.src) return;
    const idx = viewable.findIndex((g) => g.src === img.src);
    if (idx >= 0) setLightboxIndex(idx);
  };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null);
      else if (e.key === "ArrowRight")
        setLightboxIndex((i) => (i === null ? null : (i + 1) % viewable.length));
      else if (e.key === "ArrowLeft")
        setLightboxIndex((i) =>
          i === null ? null : (i - 1 + viewable.length) % viewable.length,
        );
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIndex, viewable.length]);

  if (GALLERY.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/70 bg-muted/40 px-6 py-16 text-center text-muted-foreground">
        {GALLERY_EMPTY_LABEL[lang]}
      </div>
    );
  }
  const renderTile = (img: { src?: string; alt: string; placeholder?: boolean }, key: string, featured = false) => (
    <div
      key={key}
      className={`overflow-hidden rounded-xl bg-muted ${featured ? "col-span-2 row-span-2" : ""}`}
    >
      {img.placeholder || !img.src ? (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/10 via-muted to-primary/5 p-6 text-center text-sm font-medium text-muted-foreground">
          {img.alt}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => openLightbox(img)}
          aria-label={`Enlarge photo: ${img.alt}`}
          className="block h-full w-full cursor-zoom-in overflow-hidden"
        >
          <img
            src={img.src}
            alt={img.alt}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition duration-700 hover:scale-105"
          />
        </button>
      )}
    </div>
  );
  return (
    <div>
      <div
        role="tablist"
        aria-label="Gallery categories"
        className="-mx-6 mb-6 flex gap-2 overflow-x-auto px-6 pb-2 sm:mx-0 sm:flex-wrap sm:justify-start sm:px-0 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {categories.map((cat) => {
          const isActive = active === cat;
          return (
            <button
              key={cat}
              type="button"
              role="tab"
              onClick={() => selectCategory(cat)}
              aria-selected={isActive}
              className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition duration-200 ${
                isActive
                  ? "border-primary bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/30 scale-[1.03]"
                  : "border-border bg-background text-foreground hover:border-primary hover:text-primary hover:-translate-y-0.5"
              }`}
            >
              {labels[cat]}
            </button>
          );
        })}
      </div>
      {subs && (
        <div
          role="tablist"
          aria-label="Room"
          className="-mx-6 mb-6 flex gap-2 overflow-x-auto px-6 pb-2 sm:mx-0 sm:flex-wrap sm:justify-start sm:px-0 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {(["all", ...subs] as (GallerySub | "all")[]).map((s) => {
            const isActive = activeSub === s;
            const label = s === "all" ? GALLERY_CATEGORY_LABELS[lang].all : GALLERY_SUB_LABELS[lang][s];
            return (
              <button
                key={s}
                type="button"
                role="tab"
                onClick={() => setActiveSub(s)}
                aria-selected={isActive}
                className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition duration-200 ${
                  isActive
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-primary hover:text-primary"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/70 bg-muted/40 px-6 py-16 text-center text-muted-foreground">
          {GALLERY_EMPTY_LABEL[lang]}
        </div>
      ) : active === "all" ? (
        <Suspense
          fallback={<div className="h-[260px] w-full animate-pulse rounded-xl bg-muted sm:h-[340px]" />}
        >
          <GalleryCarousel images={filtered} onOpen={openLightbox} />
        </Suspense>
      ) : (
        <div className="grid auto-rows-[220px] grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:auto-rows-[260px]">
          {filtered.map((img, i) =>
            renderTile(img, img.src ?? `${img.category}-ph-${i}`, i === 0),
          )}
        </div>
      )}
      {lightboxIndex !== null && viewable[lightboxIndex] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged photo"
          onClick={() => setLightboxIndex(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 sm:p-8 animate-in fade-in"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(null);
            }}
            aria-label="Close"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          {viewable.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(
                    (i) => (i === null ? null : (i - 1 + viewable.length) % viewable.length),
                  );
                }}
                aria-label="Previous photo"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20 sm:left-6"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(
                    (i) => (i === null ? null : (i + 1) % viewable.length),
                  );
                }}
                aria-label="Next photo"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20 sm:right-6"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
          <img
            src={viewable[lightboxIndex].src}
            alt={viewable[lightboxIndex].alt}
            onClick={(e) => e.stopPropagation()}
            decoding="async"
            fetchPriority="high"
            className="max-h-[90vh] max-w-[95vw] rounded-lg object-contain shadow-2xl"
          />
        </div>
      )}
      <BackToTop />
    </div>
  );
}

function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      type="button"
      aria-label="Back to top"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={`fixed bottom-6 right-6 z-40 grid h-11 w-11 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/20 transition-all duration-300 ease-out hover:brightness-110 ${
        visible ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 translate-y-2"
      }`}
    >
      <ChevronUp className="h-5 w-5" />
    </button>
  );
}
